import { Capacitor } from '@capacitor/core';
import { LocalNotifications, type LocalNotificationSchema } from '@capacitor/local-notifications';
import type { Cita, Pago, Tarea } from '../types';

const CANAL_RECORDATORIOS = 'recordatorios';
const CANAL_ALARMA_URGENTE = 'alarma-urgente';

// LocalNotifications solo está implementado en Android/iOS nativo. En el
// navegador (web) las llamadas tiran "Not implemented on web". Esto evita el
// stack trace feo en consola y deja las funciones como no-ops.
const SOPORTADO = Capacitor.isNativePlatform();

function obtenerBaseId(citaId: string): number {
  // Toma los últimos 8 hex chars del UUID (32 bits) y los acota a 5 dígitos
  // decimales para dejar espacio a baseId+1/+2/+3 sin chocar con otras citas.
  const hex = citaId.replace(/-/g, '').slice(-8);
  const num = parseInt(hex, 16);
  return Number.isFinite(num) ? num % 100000 : 0;
}

export async function crearCanalesNotificacion(): Promise<void> {
  if (!SOPORTADO) return;
  try {
    await LocalNotifications.createChannel({
      id: CANAL_RECORDATORIOS,
      name: 'Recordatorios',
      description: 'Notificaciones normales de recordatorio',
      importance: 4,
      sound: 'default',
      vibration: true,
    });

    await LocalNotifications.createChannel({
      id: CANAL_ALARMA_URGENTE,
      name: 'Alarma Urgente',
      description: 'Alarma de cita inminente - Máxima prioridad',
      importance: 5,
      sound: 'default',
      vibration: true,
      lights: true,
      lightColor: '#FF0000',
      visibility: 1,
    });

    console.log('✅ Canales de notificación creados');
  } catch (error) {
    console.log('Canales ya existen o error al crear:', error);
  }
}

export async function cancelarNotificacionesDeCita(citaId: string): Promise<void> {
  if (!SOPORTADO) return;
  const baseId = obtenerBaseId(citaId);
  await LocalNotifications.cancel({
    notifications: [{ id: baseId + 1 }, { id: baseId + 2 }, { id: baseId + 3 }],
  });
}

async function asegurarPermisos(soloVerificar = false): Promise<boolean> {
  let permisos = await LocalNotifications.checkPermissions();
  if (permisos.display !== 'granted' && !soloVerificar) {
    permisos = await LocalNotifications.requestPermissions();
  }
  return permisos.display === 'granted';
}

export interface OpcionesProgramar {
  // En modo silencioso no pide permisos ni muestra alerts. Usado por el sync
  // worker para programar notificaciones de citas que llegan del server sin
  // interrumpir al usuario en cada pull.
  silencioso?: boolean;
}

export async function programarNotificaciones(
  cita: Cita,
  opciones: OpcionesProgramar = {}
): Promise<void> {
  if (!SOPORTADO) return;
  const silencioso = opciones.silencioso ?? false;
  try {
    if (!(await asegurarPermisos(silencioso))) {
      if (!silencioso) {
        alert('⚠️ Necesitas activar los permisos de notificaciones para recibir recordatorios');
      }
      return;
    }

    const [year, month, day] = cita.fecha.split('-').map(Number);
    const [hours, minutes] = cita.hora.split(':').map(Number);
    const fechaCita = new Date(year, month - 1, day, hours, minutes);
    const baseId = obtenerBaseId(cita.id);

    console.log('📅 Programando notificaciones para:', cita.cliente);

    try {
      await cancelarNotificacionesDeCita(cita.id);
    } catch {
      console.log('No había notificaciones previas');
    }

    const notificaciones: LocalNotificationSchema[] = [];

    // allowWhileIdle: true es clave para que Android dispare la notificacion
    // aunque el dispositivo este en Doze (pantalla apagada por horas). Sin esto,
    // Android batches y las alarmas se posponen a la siguiente ventana de
    // mantenimiento — el sintoma tipico es "solo suenan cuando abro la app".
    // Limite: max una por cada 9 min por app (Android >= 23). Suficiente para
    // nuestras 3 notificaciones espaciadas (8am, -90 min, -15 min).
    const notifManana = new Date(fechaCita);
    notifManana.setHours(8, 0, 0, 0);
    if (notifManana > new Date()) {
      notificaciones.push({
        id: baseId + 1,
        title: '☀️ Recordatorio de Cita Hoy',
        body: `Tienes cita con ${cita.cliente} a las ${cita.hora}`,
        schedule: { at: notifManana, allowWhileIdle: true },
        sound: 'default',
        channelId: CANAL_RECORDATORIOS,
        actionTypeId: '',
        extra: null,
      });
      console.log('✅ Notificación matutina programada');
    }

    const notif90min = new Date(fechaCita.getTime() - 90 * 60 * 1000);
    if (notif90min > new Date()) {
      notificaciones.push({
        id: baseId + 2,
        title: '⏰ Cita en 1.5 horas',
        body: `${cita.cliente} - ${cita.hora}`,
        schedule: { at: notif90min, allowWhileIdle: true },
        sound: 'default',
        channelId: CANAL_RECORDATORIOS,
        actionTypeId: '',
        extra: null,
      });
      console.log('✅ Notificación 90min programada');
    }

    const notif15min = new Date(fechaCita.getTime() - 15 * 60 * 1000);
    if (notif15min > new Date()) {
      notificaciones.push({
        id: baseId + 3,
        title: '🚨🚨🚨 ¡CITA EN 15 MINUTOS! 🚨🚨🚨',
        body: `${cita.cliente} te está esperando - ¡PREPÁRATE YA!`,
        schedule: { at: notif15min, allowWhileIdle: true },
        sound: 'default',
        channelId: CANAL_ALARMA_URGENTE,
        actionTypeId: '',
        extra: null,
        attachments: [],
        autoCancel: false,
        ongoing: true,
      });
      console.log('✅ 🚨 ALARMA URGENTE 15min programada');
    }

    if (notificaciones.length > 0) {
      await LocalNotifications.schedule({ notifications: notificaciones });
      console.log(`✅ ${notificaciones.length} notificaciones programadas`);
      if (!silencioso) {
        alert(`✅ ${notificaciones.length} recordatorios programados exitosamente`);
      }
    } else if (!silencioso) {
      alert('⚠️ La cita es muy pronto. No se programaron recordatorios.');
    }
  } catch (error) {
    console.error('❌ Error:', error);
    if (!silencioso) {
      const msg = error instanceof Error ? error.message : String(error);
      alert(`Error: ${msg}`);
    }
  }
}

// Pagos y tareas comparten el espacio de IDs de notificación con las citas
// (obtenerBaseId mapea el UUID a 5 dígitos). Las citas usan baseId+1/+2/+3; aquí
// usamos offsets distintos para que un pago/tarea no pise a una cita que casualmente
// caiga en el mismo baseId.
const OFFSET_PAGO = 4;
const OFFSET_TAREA = 5;

function aOcho(fechaLimite: string): Date {
  // Recordatorio a las 8:00 AM del día de la fecha límite (YYYY-MM-DD).
  const [year, month, day] = fechaLimite.split('-').map(Number);
  return new Date(year, month - 1, day, 8, 0, 0, 0);
}

async function programarRecordatorioDia(
  notifId: number,
  fechaLimite: string,
  title: string,
  body: string,
  silencioso: boolean
): Promise<void> {
  if (!SOPORTADO) return;
  try {
    if (!(await asegurarPermisos(silencioso))) {
      if (!silencioso) {
        alert('⚠️ Necesitas activar los permisos de notificaciones para recibir recordatorios');
      }
      return;
    }
    await LocalNotifications.cancel({ notifications: [{ id: notifId }] });
    const at = aOcho(fechaLimite);
    if (at <= new Date()) {
      if (!silencioso) alert('⚠️ La fecha límite ya pasó. No se programó recordatorio.');
      return;
    }
    await LocalNotifications.schedule({
      notifications: [
        {
          id: notifId,
          title,
          body,
          schedule: { at, allowWhileIdle: true },
          sound: 'default',
          channelId: CANAL_RECORDATORIOS,
          actionTypeId: '',
          extra: null,
        },
      ],
    });
    if (!silencioso) alert('✅ Recordatorio programado');
  } catch (error) {
    console.error('❌ Error al programar recordatorio:', error);
    if (!silencioso) {
      const msg = error instanceof Error ? error.message : String(error);
      alert(`Error: ${msg}`);
    }
  }
}

export async function programarNotificacionPago(
  pago: Pago,
  opciones: OpcionesProgramar = {}
): Promise<void> {
  const silencioso = opciones.silencioso ?? false;
  const monto = pago.monto.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
  await programarRecordatorioDia(
    obtenerBaseId(pago.id) + OFFSET_PAGO,
    pago.fechaLimite,
    '💸 Pago pendiente hoy',
    `${pago.destinatario} — ${monto}`,
    silencioso
  );
}

export async function cancelarNotificacionDePago(pagoId: string): Promise<void> {
  if (!SOPORTADO) return;
  await LocalNotifications.cancel({
    notifications: [{ id: obtenerBaseId(pagoId) + OFFSET_PAGO }],
  });
}

export async function programarNotificacionTarea(
  tarea: Tarea,
  opciones: OpcionesProgramar = {}
): Promise<void> {
  const silencioso = opciones.silencioso ?? false;
  // Solo tiene sentido si la tarea tiene fecha límite y no está hecha.
  if (!tarea.fechaLimite || tarea.hecha) {
    await cancelarNotificacionDeTarea(tarea.id);
    return;
  }
  await programarRecordatorioDia(
    obtenerBaseId(tarea.id) + OFFSET_TAREA,
    tarea.fechaLimite,
    '📝 Tarea para hoy',
    tarea.titulo,
    silencioso
  );
}

export async function cancelarNotificacionDeTarea(tareaId: string): Promise<void> {
  if (!SOPORTADO) return;
  await LocalNotifications.cancel({
    notifications: [{ id: obtenerBaseId(tareaId) + OFFSET_TAREA }],
  });
}

export async function probarNotificacion(): Promise<void> {
  if (!SOPORTADO) {
    alert('Las notificaciones solo funcionan en la app Android, no en navegador');
    return;
  }
  try {
    const permisos = await LocalNotifications.requestPermissions();
    if (permisos.display !== 'granted') {
      alert('Permisos denegados');
      return;
    }

    await LocalNotifications.schedule({
      notifications: [
        {
          id: 999,
          title: '🧪 Prueba de Notificación',
          body: 'Si ves esto, las notificaciones funcionan correctamente',
          schedule: { at: new Date(Date.now() + 5000), allowWhileIdle: true },
          sound: 'default',
          channelId: CANAL_RECORDATORIOS,
        },
      ],
    });
    alert('Notificación de prueba programada para dentro de 5 segundos');
  } catch (error) {
    console.error('Error en prueba:', error);
    const msg = error instanceof Error ? error.message : String(error);
    alert(`Error: ${msg}`);
  }
}

export async function probarAlarma(): Promise<void> {
  if (!SOPORTADO) {
    alert('Las alarmas solo funcionan en la app Android, no en navegador');
    return;
  }
  try {
    const permisos = await LocalNotifications.requestPermissions();
    if (permisos.display !== 'granted') return;

    await LocalNotifications.schedule({
      notifications: [
        {
          id: 998,
          title: '🚨 Prueba de ALARMA URGENTE',
          body: 'Esta es una alarma de máxima prioridad',
          schedule: { at: new Date(Date.now() + 5000), allowWhileIdle: true },
          sound: 'default',
          channelId: CANAL_ALARMA_URGENTE,
          autoCancel: false,
          ongoing: true,
        },
      ],
    });
    alert('⏰ ALARMA de prueba programada para dentro de 5 segundos');
  } catch (error) {
    console.error('Error:', error);
    const msg = error instanceof Error ? error.message : String(error);
    alert(`Error: ${msg}`);
  }
}
